import { ReactNode, useMemo } from 'react'

import { urlFilterAdapter } from '../adapters/urlFilterAdapter'
import { FilterAdapter } from '../adapters/filterAdapter.types'
import { InsightsColourMap } from '../utils/colourResolver'
import { FilterValues } from '../types/insights.types'
import { InsightsContext, InsightsService } from './InsightsContext'

// Stable default so omitting `lockedFilters` doesn't bust the context memo each render.
const NO_LOCKED_FILTERS: FilterValues = {}

export type InsightsDomainProps = {
  /** The insights domain to scope this subtree to, e.g. `donations`. */
  domain: string
  /** The app-supplied HTTP boundary that calls the generic insights endpoints. */
  service: InsightsService
  /**
   * BCP-47 locale for number/currency/date formatting (e.g. `de-DE`). Pass the
   * host's active i18n locale. Defaults to `en-US`.
   */
  locale?: string
  /** Optional per-domain series-key → colour overrides (e.g. locked brand colours). */
  colourMap?: InsightsColourMap
  /**
   * Canonical series-key order shared by every chart in this domain. Defaults to
   * the `colourMap` key order, so defining colours in the desired order also fixes
   * the series order. Keys not listed sort after, in first-seen order.
   */
  seriesOrder?: string[]
  /** Filter-value backing store. Defaults to the URL-query-param adapter. */
  filterAdapter?: FilterAdapter
  /**
   * Filter values always applied to every chart/metric and locked from the UI:
   * `<Insights.Filters>` hides their controls and they survive Reset. Use to
   * permanently scope this panel, e.g. `lockedFilters={{ category: ['donations'] }}`.
   * Keyed like all filter values — a dateRange is locked via its `period` or
   * `from`/`to` keys, not `dateRange`. Pass a **stable reference** (module scope
   * or memoised) to avoid re-fetches.
   */
  lockedFilters?: FilterValues
  children: ReactNode
}

/**
 * Scopes a subtree to one insights domain and centralises everything its
 * components need: the domain id, the API boundary, the colour map, and the
 * filter-state adapter. Set `domain` once here and `<Insights.Filters>` /
 * `<Insights.Card>` / `<Insights.Chart>` and `useFilters()` pick it up from
 * context — no prop drilling.
 *
 * Pass **stable references** for `service`, `colourMap`, and `filterAdapter`:
 * the context value is memoised on them, so an inline `colourMap={{ ... }}` (or
 * a service rebuilt each render) busts the context and re-fetches every chart on
 * every parent render. Define them at module scope or memoise them in the host.
 */
export function InsightsDomain({
  domain,
  service,
  colourMap = {},
  seriesOrder,
  filterAdapter = urlFilterAdapter,
  lockedFilters = NO_LOCKED_FILTERS,
  locale = 'en-US',
  children,
}: Readonly<InsightsDomainProps>) {
  const value = useMemo(
    () => ({
      domain,
      service,
      colourMap,
      seriesOrder: seriesOrder ?? Object.keys(colourMap),
      filterAdapter,
      lockedFilters,
      locale,
    }),
    [
      domain,
      service,
      colourMap,
      seriesOrder,
      filterAdapter,
      lockedFilters,
      locale,
    ]
  )

  return (
    <InsightsContext.Provider value={value}>
      {children}
    </InsightsContext.Provider>
  )
}
